import { SidebarContainer } from '@affine/core/modules/app-sidebar/views';
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';

import {
  type WorkspaceAppSidebarElementProps,
  WorkspaceAppSidebarFrame,
  WorkspaceAppSidebarHeader,
} from '../workspace-app-sidebar';
import { bottomContainer, sidebarContentPanel } from './index.css';
import * as railStyles from './ui-redesign-rail.css';

export interface NavigationOverlayPaneProps {
  children: ReactNode;
  collapseLabel?: string;
  closeLabel: string;
  contentClassName?: string;
  contentProps?: WorkspaceAppSidebarElementProps;
  footer?: ReactNode;
  headerControl?: 'close' | 'collapse' | 'external';
  headerClassName?: string;
  mobileOpen: boolean;
  onClose: () => void;
  open: boolean;
  paneClassName?: string;
  testId: string;
  title: ReactNode;
  twoPane?: boolean;
}

/** Shared shell for rail-owned secondary navigation panes. */
export function NavigationOverlayPane({
  children,
  collapseLabel,
  closeLabel,
  contentClassName,
  contentProps,
  footer,
  headerControl,
  headerClassName,
  mobileOpen,
  onClose,
  open,
  paneClassName,
  testId,
  title,
  twoPane = false,
}: NavigationOverlayPaneProps): ReactElement {
  const resolvedHeaderControl =
    headerControl ?? (twoPane ? 'external' : 'close');

  return (
    <WorkspaceAppSidebarFrame
      as="div"
      className={clsx(railStyles.docsPanel, paneClassName)}
      contentClassName={clsx(
        sidebarContentPanel,
        railStyles.docsPanelContent,
        contentClassName
      )}
      contentProps={contentProps}
      footer={
        footer === undefined ? undefined : (
          <SidebarContainer className={bottomContainer}>
            {footer}
          </SidebarContainer>
        )
      }
      header={
        <WorkspaceAppSidebarHeader
          className={headerClassName}
          control={
            resolvedHeaderControl === 'external'
              ? { type: 'external' }
              : resolvedHeaderControl === 'collapse'
                ? {
                    label: collapseLabel ?? closeLabel,
                    onClick: onClose,
                    type: 'collapse',
                  }
                : {
                    label: closeLabel,
                    onClick: onClose,
                    type: 'close',
                    visibility: 'two-pane-overlay',
                  }
          }
          title={title}
        />
      }
      rootProps={{
        'data-open': open,
        'data-mobile-open': mobileOpen,
        'data-two-pane': twoPane,
        'data-testid': testId,
        inert: open || twoPane ? undefined : true,
      }}
    >
      {children}
    </WorkspaceAppSidebarFrame>
  );
}
